import React, { useEffect, useState, useCallback } from "react";
import styles from "./FixedAssetstable.module.css";
import Pagination from "./Pagination";
import { fixedAssetListing } from "../../../Bookclosure/Services/ApiHandler";
import ErrorPopup from "../../elements/ErrorPopup";

const FixedAssetTable = ({ FixedAssetID, filters = [], onNoDataFound }) => {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [errorPopup, setErrorPopup] = useState({
    isOpen: false,
    title: "No Data Found",
    message: "",
  });
  
  const formatHeading = (text) =>
    text
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim();
  
  const loadData = useCallback(async () => {
    if (!FixedAssetID) {
      setError("FixedAssetID is missing.");
      setData([]);
      setColumns([]);
      setTotalPages(1);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fixedAssetListing(FixedAssetID, {
        page: currentPage,
        pageSize: rowsPerPage,
        filters: activeFilters,
      });

      const res = response?.data || {};
      const fetchedData = res.fixedAssetListing || [];

      let headers = res.fixedAssetListingHeaders || [];
      if (headers.length === 0 && fetchedData.length > 0) {
        headers = Object.keys(fetchedData[0] || {});
      }

      setColumns(headers);
      setData(fetchedData);

      const totalPagesFromBackend = Number(res.totalPages || res.totalpages || 0);
      const computedTotalPages =
        totalPagesFromBackend > 0
          ? totalPagesFromBackend
          : Math.max(1, Math.ceil((fetchedData.length || 0) / rowsPerPage));

      setTotalPages(computedTotalPages);

      if (fetchedData.length === 0) {
        const message = "No fixed asset records found for the selected criteria.";
        setError(message);

        if (activeFilters.length > 0) {
          setErrorPopup({
            isOpen: true,
            title: "No Data Found",
            message:
              "Please check if you entered the correct values for the selected filter.",
          });
        }
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load fixed asset data. Please retry.");
      setData([]);
      setColumns([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [FixedAssetID, currentPage, rowsPerPage, activeFilters]);
  
  useEffect(() => {
    setActiveFilters(filters);
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  return (
    <div className={styles.wrapper}>
      <div className={styles.tableContainer}>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center" }}>Loading fixed asset data...</div>
        ) : error ? (
          <div style={{ padding: 20, textAlign: "center", color: "red" }}>{error}</div>
        ) : columns.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center" }}>No fixed asset columns available.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>
                    {formatHeading(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.length > rowsPerPage 
                  ? data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) 
                  : data
               ).map((row, i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col}>
                      {row[col] ?? "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      <div className={styles.paginationWrapper}>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(rows) => {
            setRowsPerPage(rows);
            setCurrentPage(1);
          }}
        />
      </div>

      <ErrorPopup
        isOpen={errorPopup.isOpen}
        title={errorPopup.title}
        message={errorPopup.message}
        onClose={() => {
          setErrorPopup({
            isOpen: false,
            title: "No Data Found",
            message: "",
          });
          if (typeof onNoDataFound === "function") {
            onNoDataFound();
          }
        }}
      />
    </div>
  );
};

export default FixedAssetTable;